import { ArrowRight, Phone, Mail, Calendar, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/animations/ScrollAnimations";
import { Link } from "react-router-dom";

export function CTASectionNew() {
  return (
    <section className="py-32 relative overflow-hidden bg-background">
      {/* Background - Premium gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5" />
      
      {/* Ambient orbs */}
      <motion.div 
        className="absolute top-1/4 right-1/4 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ 
          background: 'radial-gradient(circle, hsl(var(--primary) / 0.12) 0%, transparent 60%)',
          filter: 'blur(80px)'
        }}
        animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.6, 0.4] }}
        transition={{ duration: 10, repeat: Infinity }}
      />
      <motion.div 
        className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{ 
          background: 'radial-gradient(circle, hsl(var(--accent) / 0.1) 0%, transparent 60%)',
          filter: 'blur(60px)'
        }}
        animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 8, repeat: Infinity, delay: 1 }}
      />

      {/* Section borders */}
      <div className="absolute top-0 left-0 w-full section-divider" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Main CTA - More impactful */}
          <ScrollReveal className="text-center mb-20">
            <motion.div 
              className="inline-flex items-center gap-2 badge-primary mb-8"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Get Started
            </motion.div>
            
            <h2 className="text-4xl md:text-5xl lg:text-7xl font-bold mb-8 tracking-tight text-foreground">
              Ready to Power{" "}
              <span className="text-gradient">Your Future?</span>
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-12 font-light leading-relaxed">
              Join 150+ enterprises already transforming their EV infrastructure with our platform. 
              Let's build the future of mobility together.
            </p>
            
            <motion.div 
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
                <a href="https://outlook.office365.com/book/EvGatewayPortalDemos@evgateway.com/" target="_blank" rel="noopener noreferrer">
                  <Button variant="hero" size="xl" className="group shadow-lg shadow-primary/20 text-base px-10 py-7">
                    Request Demo
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                </a>
              </motion.div>
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
                <Button 
                  variant="outline" 
                  size="xl" 
                  className="border-foreground/30 text-foreground hover:bg-foreground/10 hover:border-foreground/50 transition-all text-base px-10 py-7" 
                  asChild
                >
                  <Link to="/contact">Contact Sales</Link>
                </Button>
              </motion.div>
            </motion.div>
          </ScrollReveal>

          {/* Contact Options - Premium cards */}
          <StaggerContainer className="grid md:grid-cols-3 gap-6" staggerDelay={0.1}>
            {[
              {
                icon: Calendar,
                title: "Book a Demo",
                description: "See our platform in action with a personalized walkthrough",
                action: "Schedule Now",
                color: "primary",
                link: "https://outlook.office365.com/book/EvGatewayPortalDemos@evgateway.com/"
              },
              {
                icon: Phone,
                title: "Talk to Sales",
                description: "Connect with our solution experts for custom requirements",
                action: "Contact Us",
                color: "accent",
                link: "/contact",
                isInternal: true
              },
              {
                icon: Mail,
                title: "Email Us",
                description: "Get a detailed proposal tailored to your needs",
                action: "sales@evgateway.com",
                color: "primary"
              },
            ].map((option, idx) => (
              <StaggerItem key={idx}>
                {option.link ? (
                  option.isInternal ? (
                    <Link to={option.link}>
                      <motion.div
                        whileHover={{ scale: 1.02, y: -6 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className={`group glass-card p-8 hover:border-${option.color}/30 transition-all text-center cursor-pointer relative overflow-hidden`}
                      >
                        <motion.div
                          className={`absolute -top-20 -right-20 w-40 h-40 bg-${option.color}/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                        />
                        <motion.div 
                          className={`w-14 h-14 rounded-2xl bg-${option.color}/10 border border-${option.color}/20 flex items-center justify-center mx-auto mb-5 relative z-10`}
                          whileHover={{ scale: 1.1, rotate: 5 }}
                        >
                          <option.icon className={`h-7 w-7 text-${option.color}`} />
                        </motion.div>
                        <h3 className="font-semibold text-xl mb-2 relative z-10 text-card-foreground">{option.title}</h3>
                        <p className="text-sm text-white/70 mb-4 leading-relaxed font-light relative z-10">{option.description}</p>
                        <span className={`text-${option.color} font-semibold text-sm relative z-10`}>{option.action}</span>
                      </motion.div>
                    </Link>
                  ) : (
                    <a href={option.link} target="_blank" rel="noopener noreferrer">
                      <motion.div
                        whileHover={{ scale: 1.02, y: -6 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className={`group glass-card p-8 hover:border-${option.color}/30 transition-all text-center cursor-pointer relative overflow-hidden`}
                      >
                        <motion.div
                          className={`absolute -top-20 -right-20 w-40 h-40 bg-${option.color}/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                        />
                        <motion.div 
                          className={`w-14 h-14 rounded-2xl bg-${option.color}/10 border border-${option.color}/20 flex items-center justify-center mx-auto mb-5 relative z-10`}
                          whileHover={{ scale: 1.1, rotate: 5 }}
                        >
                          <option.icon className={`h-7 w-7 text-${option.color}`} />
                        </motion.div>
                        <h3 className="font-semibold text-xl mb-2 relative z-10 text-card-foreground">{option.title}</h3>
                        <p className="text-sm text-white/70 mb-4 leading-relaxed font-light relative z-10">{option.description}</p>
                        <span className={`text-${option.color} font-semibold text-sm relative z-10`}>{option.action}</span>
                      </motion.div>
                    </a>
                  )
                ) : (
                  <motion.div
                    whileHover={{ scale: 1.02, y: -6 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className={`group glass-card p-8 hover:border-${option.color}/30 transition-all text-center relative overflow-hidden`}
                  >
                    <motion.div
                      className={`absolute -top-20 -right-20 w-40 h-40 bg-${option.color}/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                    />
                    <motion.div 
                      className={`w-14 h-14 rounded-2xl bg-${option.color}/10 border border-${option.color}/20 flex items-center justify-center mx-auto mb-5 relative z-10`}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      <option.icon className={`h-7 w-7 text-${option.color}`} />
                    </motion.div>
                    <h3 className="font-semibold text-xl mb-2 relative z-10 text-card-foreground">{option.title}</h3>
                    <p className="text-sm text-white/70 mb-4 leading-relaxed font-light relative z-10">{option.description}</p>
                    <span className={`text-${option.color} font-semibold text-sm relative z-10`}>{option.action}</span>
                  </motion.div>
                )}
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 w-full section-divider" />
    </section>
  );
}